import type { OpenTab } from '@/components/features/document-tabs'

/** Minimal tab shape for open-intent resolution (avoids coupling to full OpenTab). */
export type OpenIntentCurrentTab = Pick<OpenTab, 'id' | 'type' | 'isPinned'> | null | undefined

export type OpenIntentClickEvent =
  | Partial<Pick<MouseEvent, 'metaKey' | 'ctrlKey' | 'button'>>
  | undefined
  | null

/** True when event is Cmd/Ctrl-click (open in new tab). */
export function isModClick(event?: OpenIntentClickEvent): boolean {
  return Boolean(event?.metaKey || event?.ctrlKey)
}

export function isHomeTab(
  currentTab?: OpenIntentCurrentTab,
  activeTabId?: string | null,
): boolean {
  return currentTab?.type === 'home' || activeTabId === 'home'
}

/** Workspace dashboard tab - same open semantics as Home (never replace). */
export function isWorkspaceHomeTab(
  currentTab?: OpenIntentCurrentTab,
  activeTabId?: string | null,
): boolean {
  return (
    currentTab?.type === 'workspace-home' ||
    Boolean(activeTabId?.startsWith('workspace-home-'))
  )
}

/**
 * Utility views (search, edit-history, graph) stay open beside a document.
 * A plain click from those tabs always opens a new tab.
 * People uses the same slot history as a document: a plain open replaces
 * the People tab so Back returns to it. Cmd-click still opens a new tab.
 */
export function isUtilityViewTab(currentTab?: OpenIntentCurrentTab): boolean {
  const type = currentTab?.type
  return type === 'search-view' || type === 'edit-history' || type === 'graph-view'
}

/**
 * Resolve whether an open gesture should create/reuse a new tab or replace the current one.
 * See ADR-0016 and docs/product/bets/2026-05-30-unified-interaction-primitives.md.
 */
export function resolveOpenIntent(
  event: OpenIntentClickEvent,
  options: { currentTab?: OpenIntentCurrentTab; activeTabId?: string | null },
): 'new' | 'replace' {
  if (isModClick(event)) return 'new'
  if (options.currentTab?.isPinned === true) return 'new'
  if (isHomeTab(options.currentTab, options.activeTabId)) return 'new'
  if (isWorkspaceHomeTab(options.currentTab, options.activeTabId)) return 'new'
  if (isUtilityViewTab(options.currentTab)) return 'new'
  return 'replace'
}

/** Map resolver verdict to the legacy `openInNewTab` boolean flag. */
export function openIntentToNewTabFlag(intent: 'new' | 'replace'): boolean {
  return intent === 'new'
}

/**
 * Handshake for in-app markdown / Inbox links: React Router `navigate()` then
 * url-sync `openBasecampTabFromUrl`. Cmd-click cannot pass through the URL, so
 * the click handler marks `new` and the next url-sync consume honors it.
 */
let nextUrlOpenIntent: 'new' | 'replace' | null = null

export function markNextUrlOpenIntent(intent: 'new' | 'replace'): void {
  nextUrlOpenIntent = intent
}

export function consumeNextUrlOpenIntent(): 'new' | 'replace' | null {
  const value = nextUrlOpenIntent
  nextUrlOpenIntent = null
  return value
}
